import i18n from '../../../i18n';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import AppBreadcrumb from "../../Shared/AppBreadcrumb";
import FecthingSpinner from "../../Shared/FecthingSpinner";
import httpService from "../../../services/http.service";
import MyOpinionLogo from "../../../assets/images/MyOpinion_Logo_Head.png";

const DEFAULT_SUPPORT = "payin";
const DEFAULT_PLATFORM = "all";

const SUPPORT_LABELS = {
  payin: "Encaissement",
  payout: "Décaissement",
  both: "Encaissement & décaissement",
};

const PLATFORM_LABELS = {
  all: "Toutes plateformes",
  web: "Web",
  android: "Android",
  ios: "iOS",
};

function labelSupport(v) {
  if (!v) return "—";
  return i18n.t(SUPPORT_LABELS[v] || v);
}

function labelPlatform(v) {
  if (!v) return "—";
  return i18n.t(PLATFORM_LABELS[v] || v);
}

function buildPayloadMethods(methods) {
  return methods.map((m) => {
    const base = {
      method_id: m.method_id,
      is_available: !!m.is_available,
      method_support: m.method_support || DEFAULT_SUPPORT,
      method_platform: m.method_platform || DEFAULT_PLATFORM,
    };

    let fees = Array.isArray(m.fees) ? m.fees : [];
    fees = fees.filter(
      (fee) =>
        fee &&
        fee.provider_id &&
        fee.payment_type &&
        fee.fee_type &&
        fee.currency_code
    );

    return { ...base, fees };
  });
}

/**
 * Activer ou retirer des méthodes pour un pays (cases à cocher).
 * GET/PUT /payment-countries/:id/methods-config
 */
const PaymentCountryMethodActivation = ({ match, history }) => {
    const { t } = useTranslation();
  const { id } = match.params;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [country, setCountry] = useState(null);
  const [methods, setMethods] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await httpService.get(
        `/payment-countries/${id}/methods-config`
      );
      const payload = response?.data?.data || {};
      setCountry(payload.country || null);

      const raw = Array.isArray(payload.methods) ? payload.methods : [];
      setMethods(
        raw.map((m) => ({
          ...m,
          is_available: !!m.is_available,
          method_support: m.method_support || DEFAULT_SUPPORT,
          method_platform: m.method_platform || DEFAULT_PLATFORM,
          fees: Array.isArray(m.fees) ? m.fees : [],
        }))
      );
    } catch (e) {
      console.error(e);
      alert(
        i18n.t("Impossible de charger la liste des méthodes pour ce pays.")
      );
      setCountry(null);
      setMethods([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleMethod = (methodId, checked) => {
    setMethods((prev) =>
      prev.map((m) =>
        m.method_id === methodId ? { ...m, is_available: checked } : m
      )
    );
  };

  const setAllAvailable = (value) => {
    setMethods((prev) => prev.map((m) => ({ ...m, is_available: value })));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await httpService.put(`/payment-countries/${id}/methods-config`, {
        methods: buildPayloadMethods(methods),
      });
      alert(t("Disponibilité des méthodes enregistrée."));
      history.push(`/payment-countries/${id}/methods`);
    } catch (err) {
      console.error(err);
      alert(
        i18n.t("Erreur lors de l’enregistrement. Vérifiez la console ou les frais configurés.")
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container-fluid payment-method-activation-page">
        <AppBreadcrumb title={t("Activer des méthodes")} />
        <FecthingSpinner />
      </div>
    );
  }

  if (!country) {
    return (
      <div className="container-fluid payment-method-activation-page">
        <AppBreadcrumb title={t("Activer des méthodes")} />
        <div className="card border-0 shadow-sm mt-2">
          <div className="card-body text-center py-5">
            <span
              className="icon-globe text-muted d-block mb-3"
              style={{ fontSize: "2.25rem" }}
              aria-hidden
            />
            <h5 className="text-secondary">{t("Pays introuvable")}</h5>
            <p className="text-muted small mb-4">
              {t("La configuration n’a pas pu être chargée pour ce pays.")}
            </p>
            <Link
              to="/payment-countries"
              className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center"
            >
              <i className="icon-arrow-left mr-2" aria-hidden />
              {t("Liste des pays")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const activeCount = methods.filter((m) => m.is_available).length;
  const title = `Activer des méthodes — ${country.name_fr || country.code}`;

  return (
    <div className="container-fluid payment-method-activation-page">
      <AppBreadcrumb title={title}>
        <div className="col-md-6 col-sm-12 text-md-right">
          <nav className="d-inline-block text-left text-md-right small text-muted mb-0">
            <Link to="/payment-countries" className="text-muted">
              {t("Pays")}
            </Link>
            <span className="mx-1">/</span>
            <Link
              to={`/payment-countries/${id}/methods`}
              className="text-muted"
            >
              {country.code}
            </Link>
            <span className="mx-1">/</span>
            <span className="text-dark font-weight-medium">{t("Activation")}</span>
          </nav>
        </div>
      </AppBreadcrumb>

      <div
        className="card border-0 shadow-sm mb-4 overflow-hidden payment-activation-hero"
        style={{ borderLeft: "4px solid #28a745" }}
      >
        <div className="card-body p-4">
          <div className="row align-items-center">
            <div className="col-auto pr-3">
              <div
                className="rounded-lg bg-light d-flex align-items-center justify-content-center shadow-sm"
                style={{
                  width: 64,
                  height: 64,
                  overflow: "hidden",
                }}
              >
                {country.flag ? (
                  <img
                    src={country.flag}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <span className="h6 mb-0 text-muted font-weight-bold">
                    {country.code}
                  </span>
                )}
              </div>
            </div>
            <div className="col pl-0 pl-md-2">
              <p className="text-uppercase text-muted small mb-1 payment-activation-hero__kicker">
                {t("Disponibilité par pays")}
              </p>
              <h4 className="mb-2 font-weight-bold text-dark">
                {country.name_fr}
                <span className="text-muted font-weight-normal h5 mb-0">
                  {" "}
                  ({country.name_en})
                </span>
              </h4>
              <p className="text-muted small mb-2 mb-md-0 payment-activation-hero__lead">
                {t("Cochez les méthodes offertes sur ce marché. Les cases modifient la liaison pays ↔ méthode pour chaque méthode du catalogue.")}
              </p>
            </div>
            <div className="col-12 col-md-auto mt-3 mt-md-0 text-md-right">
              <div className="payment-activation-stat d-inline-block text-left text-md-right">
                <div className="text-muted small text-uppercase payment-activation-stat__label">
                  <i className="fa fa-toggle-on mr-1" aria-hidden="true" />
                  {t("Méthodes activées")}
                </div>
                <div className="payment-activation-stat__value text-success">
                  {activeCount}
                  <span className="payment-activation-stat__total text-muted">
                    {" "}
                    / {methods.length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSave}>
        <div className="card border-0 shadow-sm mb-0 payment-activation-table-card overflow-hidden">
          <div className="card-header payment-activation-table-header border-0 py-3 d-flex flex-wrap align-items-center justify-content-between">
            <div>
              <h6 className="mb-0 font-weight-bold text-dark">
                {t("Catalogue des méthodes")}
              </h6>
              <small className="text-muted">
                {t("Une ligne = une méthode ; la case « Actif » l’associe au pays.")}
              </small>
            </div>
            {methods.length > 0 ? (
              <div className="mt-2 mt-md-0 d-flex flex-wrap align-items-center payment-activation-bulk">
                <button
                  type="button"
                  className="btn btn-link btn-sm text-success px-2 py-0"
                  onClick={() => setAllAvailable(true)}
                  disabled={saving}
                >
                  Tout activer
                </button>
                <span className="text-muted mx-1">·</span>
                <button
                  type="button"
                  className="btn btn-link btn-sm text-secondary px-2 py-0"
                  onClick={() => setAllAvailable(false)}
                  disabled={saving}
                >
                  Tout retirer
                </button>
              </div>
            ) : null}
          </div>

          <div className="table-responsive payment-activation-table-wrap">
            <table className="table table-hover mb-0 payment-activation-table">
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="text-center payment-activation-th-check"
                    title={t("Disponible pour ce pays")}
                  >
                    {t("Actif")}
                  </th>
                  <th scope="col">{t("Méthode")}</th>
                  <th scope="col">{t("Référence")}</th>
                  <th scope="col">{t("Support")}</th>
                  <th scope="col">{t("Plateforme")}</th>
                  <th scope="col">{t("Catalogue")}</th>
                </tr>
              </thead>
              <tbody>
                {methods.map((method) => (
                  <tr
                    key={method.method_id}
                    className={
                      method.is_available
                        ? "payment-activation-tr payment-activation-tr--on"
                        : "payment-activation-tr"
                    }
                  >
                    <td className="text-center align-middle payment-activation-td-check">
                      <input
                        type="checkbox"
                        className="payment-activation-checkbox"
                        id={`avail-${method.method_id}`}
                        checked={!!method.is_available}
                        onChange={(e) =>
                          toggleMethod(method.method_id, e.target.checked)
                        }
                        disabled={saving}
                        aria-label={`Activer ${method.method_name} pour ce pays`}
                      />
                    </td>
                    <td className="align-middle">
                      <div className="d-flex align-items-center">
                        <img
                          src={method.method_logo || MyOpinionLogo}
                          alt=""
                          width={36}
                          height={36}
                          className="flex-shrink-0 mr-2 payment-activation-avatar"
                        />
                        <div>
                          <div className="payment-activation-method-name">
                            {method.method_name}
                          </div>
                          <span className="payment-activation-method-meta">
                            ID {method.method_id}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="align-middle">
                      <code className="payment-activation-tag">
                        {method.method_tag || "—"}
                      </code>
                    </td>
                    <td className="align-middle">
                      <span className="payment-activation-pill">
                        {labelSupport(method.method_support)}
                      </span>
                    </td>
                    <td className="align-middle">
                      <span className="payment-activation-pill">
                        {labelPlatform(method.method_platform)}
                      </span>
                    </td>
                    <td className="align-middle">
                      <span
                        className={`payment-activation-catalog ${
                          method.method_state === "available"
                            ? "is-ok"
                            : "is-warn"
                        }`}
                      >
                        {method.method_state === "available"
                          ? "Disponible"
                          : method.method_state || "—"}
                      </span>
                    </td>
                  </tr>
                ))}
                {methods.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-5">
                      <div className="text-muted small">
                        {t("Aucune méthode dans le catalogue.")}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="card-footer payment-activation-footer border-0 bg-white d-flex flex-wrap align-items-center justify-content-between py-3">
            <div className="mb-2 mb-md-0">
              <Link
                to={`/payment-countries/${id}/methods`}
                className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center"
              >
                <i className="icon-arrow-left mr-2" aria-hidden />
                {t("Annuler")}
              </Link>
            </div>
            <button
              type="submit"
              className="btn btn-success d-inline-flex align-items-center px-4 payment-activation-submit"
              disabled={saving || methods.length === 0}
            >
              {saving ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm mr-2"
                    role="status"
                  />
                  Enregistrement…
                </>
              ) : (
                <>
                  <i className="icon-check mr-2" aria-hidden />
                  {t("Enregistrer les changements")}
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      <div className="mt-3 mb-2">
        <Link
          to="/payment-countries"
          className="btn btn-link text-muted pl-0 small d-inline-flex align-items-center"
        >
          <i className="icon-arrow-left mr-1" aria-hidden />
          {t("Liste des pays de paiement")}
        </Link>
      </div>

      <style>{`
        .payment-method-activation-page .payment-activation-hero__kicker {
          letter-spacing: 0.06em;
          font-size: 0.65rem;
          font-weight: 600;
        }
        .payment-method-activation-page .payment-activation-hero__lead {
          max-width: 42rem;
          line-height: 1.45;
        }
        .payment-method-activation-page .payment-activation-stat__label {
          font-size: 0.65rem;
          letter-spacing: 0.05em;
          font-weight: 600;
        }
        .payment-method-activation-page .payment-activation-stat__value {
          font-size: 1.75rem;
          font-weight: 700;
          line-height: 1.1;
        }
        .payment-method-activation-page .payment-activation-stat__total {
          font-size: 1rem;
          font-weight: 500;
        }
        .payment-method-activation-page .payment-activation-table-card {
          border-radius: 8px;
        }
        .payment-method-activation-page .payment-activation-table-header {
          background: linear-gradient(180deg, #f8f9fb 0%, #fff 100%);
          border-bottom: 1px solid #e9ecef !important;
        }
        .payment-method-activation-page .payment-activation-table-wrap {
          border-top: 1px solid #f1f3f5;
        }
        .payment-method-activation-page .payment-activation-table {
          font-size: 0.875rem;
          border-collapse: collapse;
        }
        .payment-method-activation-page .payment-activation-table thead th {
          background: #eef1f4 !important;
          color: #495057;
          font-size: 0.65rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          border-bottom: 1px solid #dee2e6 !important;
          border-top: none !important;
          padding: 0.65rem 1rem;
          vertical-align: middle;
        }
        .payment-method-activation-page .payment-activation-th-check {
          width: 4.5rem;
        }
        .payment-method-activation-page .payment-activation-table tbody td {
          background: #fff;
          border-bottom: 1px solid #f1f3f5;
          padding: 0.65rem 1rem;
          vertical-align: middle;
        }
        .payment-method-activation-page .payment-activation-tr--on td {
          background: #f6fff8;
        }
        .payment-method-activation-page .payment-activation-tr--on td:first-child {
          box-shadow: inset 3px 0 0 #28a745;
        }
        .payment-method-activation-page .payment-activation-tr:hover td {
          background: #f8fafb;
        }
        .payment-method-activation-page .payment-activation-tr--on:hover td {
          background: #eef9f1;
        }
        .payment-method-activation-page .payment-activation-table tbody tr:last-child td {
          border-bottom: none;
        }
        .payment-method-activation-page .payment-activation-checkbox {
          width: 1.2rem;
          height: 1.2rem;
          cursor: pointer;
          accent-color: #28a745;
        }
        .payment-method-activation-page .payment-activation-checkbox:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        .payment-method-activation-page .payment-activation-avatar {
          object-fit: cover;
          border-radius: 50%;
          border: 1px solid #e9ecef;
        }
        .payment-method-activation-page .payment-activation-method-name {
          font-weight: 600;
          color: #212529;
          font-size: 0.875rem;
          line-height: 1.25;
        }
        .payment-method-activation-page .payment-activation-method-meta {
          font-size: 0.72rem;
          color: #868e96;
        }
        .payment-method-activation-page .payment-activation-tag {
          display: inline-block;
          font-size: 0.75rem;
          padding: 0.2rem 0.45rem;
          background: #f1f3f5;
          color: #343a40;
          border-radius: 4px;
          border: 1px solid #e9ecef;
        }
        .payment-method-activation-page .payment-activation-pill {
          display: inline-block;
          font-size: 0.72rem;
          font-weight: 500;
          color: #495057;
          background: #fff;
          border: 1px solid #dee2e6;
          padding: 0.2rem 0.5rem;
          border-radius: 999px;
        }
        .payment-method-activation-page .payment-activation-catalog {
          display: inline-block;
          font-size: 0.72rem;
          font-weight: 600;
          padding: 0.25rem 0.6rem;
          border-radius: 999px;
        }
        .payment-method-activation-page .payment-activation-catalog.is-ok {
          background: rgba(40, 167, 69, 0.12);
          color: #1e7e34;
        }
        .payment-method-activation-page .payment-activation-catalog.is-warn {
          background: #e9ecef;
          color: #495057;
        }
        .payment-method-activation-page .payment-activation-footer {
          border-top: 1px solid #e9ecef !important;
        }
        .payment-method-activation-page .payment-activation-submit {
          border-radius: 6px;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
};

export default PaymentCountryMethodActivation;
